import React, { useCallback, useEffect, useState } from 'react';
import { Input, Button, Checkbox } from '@/components/ui';
import { Bot, Eye, EyeOff, CheckCircle, XCircle, Globe, FlaskConical } from 'lucide-react';
import { cn } from '@/lib/utils';
import { t } from '@/lib/i18n';
import { apiFetch } from '@/lib/api';

interface AIProviderData {
  provider: string | null;
  api_key: string | null;
  website_url?: string | null;
  research_website?: boolean;
}

interface StepAIProviderProps {
  value: AIProviderData;
  onChange: (data: AIProviderData) => void;
  autoFocus?: boolean;
}

const PROVIDERS = [
  { id: 'anthropic', name: 'Anthropic (Claude)', description: 'Claude Sonnet & Opus models' },
  { id: 'openai', name: 'OpenAI (GPT)', description: 'GPT-5.4 & GPT-5.4 Pro models' },
  { id: 'gemini', name: 'Google (Gemini)', description: 'Gemini Flash & Pro models' },
] as const;

/** All option IDs in navigation order: dry_run first, then providers */
const ALL_IDS = ['__dry_run__', ...PROVIDERS.map((p) => p.id)] as const;

export function StepAIProvider({ value, onChange, autoFocus }: StepAIProviderProps) {
  const [showKey, setShowKey] = useState(false);
  const [testStatus, setTestStatus] = useState<'idle' | 'testing' | 'success' | 'error'>('idle');
  const [testError, setTestError] = useState('');
  const containerRef = React.useRef<HTMLDivElement>(null);

  const selectedId = value.provider ?? '__dry_run__';
  const isDryRunSelected = selectedId === '__dry_run__';

  // Auto-focus the first (dry run) card on mount
  useEffect(() => {
    if (autoFocus && containerRef.current) {
      requestAnimationFrame(() => {
        const tabbable = containerRef.current?.querySelector<HTMLElement>('[tabindex="0"]');
        tabbable?.focus();
      });
    }
  }, [autoFocus]);

  const selectOption = useCallback(
    (id: string) => {
      setTestStatus('idle');
      setTestError('');
      if (id === '__dry_run__') {
        onChange({ ...value, provider: null, api_key: null, research_website: false });
      } else {
        onChange({ ...value, provider: id, api_key: id === value.provider ? value.api_key : '' });
      }
    },
    [onChange, value],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent, optionId: string) => {
      const currentIdx = ALL_IDS.indexOf(optionId as (typeof ALL_IDS)[number]);
      if (currentIdx === -1) return;

      let nextIdx = currentIdx;

      switch (e.key) {
        case 'Tab':
          // Let Tab/Shift+Tab follow normal browser tab order (exit card group)
          return;
        case 'ArrowDown':
        case 'ArrowRight':
          e.preventDefault();
          nextIdx = (currentIdx + 1) % ALL_IDS.length;
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          e.preventDefault();
          nextIdx = (currentIdx - 1 + ALL_IDS.length) % ALL_IDS.length;
          break;
        case ' ':
        case 'Enter':
          e.preventDefault();
          selectOption(optionId);
          return;
        default:
          return;
      }

      // Focus the target card
      if (containerRef.current) {
        const target = containerRef.current.querySelector<HTMLElement>(
          `[data-option-id="${ALL_IDS[nextIdx]}"]`,
        );
        target?.focus();
      }
    },
    [selectOption],
  );

  const handleKeyChange = (apiKey: string) => {
    setTestStatus('idle');
    // B07 guard: if key changes, test is no longer valid → clear research checkbox
    onChange({ ...value, api_key: apiKey, research_website: false });
  };

  const handleWebsiteUrlChange = (url: string) => {
    onChange({ ...value, website_url: url || null });
  };

  const handleResearchWebsiteChange = (checked: boolean) => {
    onChange({ ...value, research_website: checked });
  };

  // B33: real provider validation — calls POST /api/v1/launchpad/ai-provider/test-connection
  const handleTestConnection = async () => {
    if (!value.api_key?.trim() || !value.provider) return;
    setTestStatus('testing');
    setTestError('');
    try {
      const res = await apiFetch('/api/v1/launchpad/ai-provider/test-connection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        // M18.2 (#21): backend now expects apiKey (camelCase)
        body: JSON.stringify({ provider: value.provider, apiKey: value.api_key.trim() }),
      });
      const json = await res.json();
      if (json.success && json.data?.valid) {
        setTestStatus('success');
      } else {
        setTestStatus('error');
        setTestError(json.data?.reason || 'Connection test failed. Please verify your API key.');
      }
    } catch {
      setTestStatus('error');
      setTestError('Connection test failed. Please verify your API key.');
    }
  };

  // B06: Research opt-in section renders whenever a real provider is selected —
  // the URL field, checkbox, and test CTA are disabled until the API key is
  // successfully tested. Previously the entire block only appeared after a
  // successful test, which made the future flow invisible to users and made
  // B05's URL-field-inside-research-block intent confusing.
  const showResearchOptIn = !isDryRunSelected;
  const researchEnabled = testStatus === 'success';

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-foreground">AI Provider Configuration</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose an AI provider for AI operators, or start in dry-run mode. You can configure this
          later.
        </p>
      </div>

      {/* ── Radiogroup: featured Dry Run card + 3-column provider row ─────── */}
      <div ref={containerRef} role="radiogroup" aria-label="AI Provider" className="space-y-4">
        {/* Featured: Dry Run — full-width card */}
        <div
          data-option-id="__dry_run__"
          role="radio"
          aria-checked={isDryRunSelected}
          tabIndex={isDryRunSelected ? 0 : -1}
          onClick={() => selectOption('__dry_run__')}
          onKeyDown={(e) => handleKeyDown(e, '__dry_run__')}
          className={cn(
            'relative text-left rounded-xl border-2 p-6 transition-all duration-200 cursor-pointer select-none',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            isDryRunSelected
              ? 'border-primary-500 bg-primary-50 shadow-md'
              : 'border-border bg-background hover:border-border hover:shadow-sm',
          )}
        >
          <div className="flex items-center gap-4">
            <div
              className={cn(
                'p-3 rounded-lg flex-shrink-0',
                isDryRunSelected
                  ? 'bg-primary-100 text-primary-600'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              <Bot className="w-7 h-7" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-base text-foreground">
                {t('No AI provider (Dry Run Mode)')}
              </h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t(
                  "AI operators log what they would do but don't execute LLM calls. Governance and workflows still work — you can add a provider later.",
                )}
              </p>
            </div>
            {/* Radio indicator */}
            <div
              className={cn(
                'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
                isDryRunSelected ? 'border-primary-500' : 'border-border',
              )}
            >
              {isDryRunSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
            </div>
          </div>
        </div>

        {/* Provider cards — 3-column row */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {PROVIDERS.map((provider) => {
            const isSelected = selectedId === provider.id;
            return (
              <div
                key={provider.id}
                data-option-id={provider.id}
                role="radio"
                aria-checked={isSelected}
                tabIndex={!isDryRunSelected && isSelected ? 0 : -1}
                onClick={() => selectOption(provider.id)}
                onKeyDown={(e) => handleKeyDown(e, provider.id)}
                className={cn(
                  'relative text-left rounded-xl border-2 p-5 transition-all duration-200 cursor-pointer select-none',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
                  isSelected
                    ? 'border-primary-500 bg-primary-50 shadow-md'
                    : 'border-border bg-background hover:border-border hover:shadow-sm',
                )}
              >
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-sm text-foreground">{t(provider.name)}</h3>
                    <p className="text-xs text-muted-foreground mt-1">{t(provider.description)}</p>
                  </div>
                  {/* Radio indicator */}
                  <div
                    className={cn(
                      'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors mt-0.5',
                      isSelected ? 'border-primary-500' : 'border-border',
                    )}
                  >
                    {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-primary-500" />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── API Key input (only when a provider is selected) ─────────────── */}
      {value.provider && (
        <div className="space-y-4 p-4 rounded-lg bg-muted border border-border">
          <div className="relative">
            <Input
              label="API Key"
              type={showKey ? 'text' : 'password'}
              value={value.api_key || ''}
              onChange={(e) => handleKeyChange(e.target.value)}
              placeholder={`Enter your ${
                PROVIDERS.find((p) => p.id === value.provider)?.name || ''
              } API key`}
              rightIcon={
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowKey(!showKey)}
                  className="h-7 w-7 text-muted-foreground hover:text-foreground"
                  aria-label={showKey ? 'Hide API key' : 'Show API key'}
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              }
            />
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleTestConnection}
              disabled={!value.api_key?.trim() || testStatus === 'testing'}
              loading={testStatus === 'testing'}
            >
              Test Connection
            </Button>

            {testStatus === 'success' && (
              <span className="flex items-center gap-1 text-sm text-success-600">
                <CheckCircle className="w-4 h-4" />
                Connection successful
              </span>
            )}
            {testStatus === 'error' && (
              <span className="flex items-center gap-1 text-sm text-error-600">
                <XCircle className="w-4 h-4" />
                {testError}
              </span>
            )}
          </div>

          {/* B33: replaced misleading "validated when complete" with accurate note */}
          <p className="text-xs text-muted-foreground" data-testid="test-connection-note">
            {t('Your API key is encrypted at rest. Test the connection above before completing onboarding.')}
          </p>

          {/* ── Research Opt-In (B06: always visible when a provider is selected; ──
             disabled until API key is tested successfully; B05: URL field lives
             inside this block, pre-filled from org.website_url, editable) ───── */}
          {showResearchOptIn && (
            <div
              className={cn(
                'mt-4 p-4 rounded-lg border border-primary-200 bg-primary-50/50 space-y-3',
                !researchEnabled && 'opacity-60',
              )}
              role="group"
              aria-labelledby="research-opt-in-title"
              data-testid="research-opt-in-section"
            >
              <div className="flex items-center gap-2">
                <FlaskConical className="w-4 h-4 text-primary-600 flex-shrink-0" />
                <h4
                  id="research-opt-in-title"
                  className="text-sm font-semibold text-primary-800"
                >
                  {t('AI-Assisted Company Profile Research')}
                </h4>
              </div>

              {/* Explanation */}
              <p className="text-sm text-muted-foreground">
                {t('When you complete onboarding, ROROS will crawl your website and use your configured AI provider to extract key company information. The results are staged for your review — nothing is published automatically.')}
              </p>

              {/* B05: Website URL lives INSIDE the research block, above the
                 checkbox. Pre-filled from organizations.website_url (seeded in
                 Launchpad.tsx). Disabled until test succeeds. */}
              <div className="space-y-1">
                <Input
                  id="website-url"
                  label={t('Company Website URL')}
                  type="url"
                  value={value.website_url ?? ''}
                  onChange={(e) => handleWebsiteUrlChange(e.target.value)}
                  placeholder={t('https://yourcompany.com')}
                  leftIcon={<Globe className="w-4 h-4 text-muted-foreground" />}
                  disabled={!researchEnabled}
                />
                <p className="text-xs text-muted-foreground pl-1">
                  {t('Used to pre-fill your company profile. You can edit the results afterwards.')}
                </p>
              </div>

              {/* Opt-in checkbox — uses boilerplate Checkbox (#19) */}
              <Checkbox
                checked={value.research_website ?? false}
                onChange={(e) => handleResearchWebsiteChange(e.target.checked)}
                disabled={!researchEnabled}
                label={t('Run AI research now (recommended)')}
              />

              {/* B06: hint shown when disabled — tells user what unblocks it. */}
              {!researchEnabled && (
                <p className="text-xs text-muted-foreground italic" data-testid="research-disabled-hint">
                  {t('Test your API key to enable research.')}
                </p>
              )}

              {/* Privacy hint */}
              <p className="text-xs text-muted-foreground">
                {t('Only publicly accessible pages are crawled. robots.txt is honoured.')}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
